import { useState, useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  TablePagination,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import PaidIcon from '@mui/icons-material/Paid';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import type { Restock, PaginatedResponse } from '../../types';
import { RestockStatus } from '../../types';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';

const fetchRestocks = async (
  page: number,
  pageSize: number,
  search?: string,
  status?: RestockStatus
): Promise<PaginatedResponse<Restock>> => {
  const params: Record<string, unknown> = {
    page: page + 1,
    limit: pageSize,
  };

  if (search) {
    params.search = search;
  }

  if (status) {
    params.status = status;
  }

  const response = await axiosInstance.get('/restocks', { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.pagination?.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const statusLabels: Record<RestockStatus, string> = {
  [RestockStatus.QUOTE]: 'Quote',
  [RestockStatus.ORDERED]: 'Ordered',
  [RestockStatus.DELIVERED]: 'Delivered',
  [RestockStatus.PAID]: 'Paid',
  [RestockStatus.CANCELLED]: 'Cancelled',
};

const getStatusColor = (status: RestockStatus) => {
  switch (status) {
    case RestockStatus.QUOTE:
      return palette.warning;
    case RestockStatus.ORDERED:
      return palette.info;
    case RestockStatus.DELIVERED:
      return palette.success;
    case RestockStatus.PAID:
      return palette.primary;
    case RestockStatus.CANCELLED:
      return palette.error;
    default:
      return palette.textMuted;
  }
};

const getStatusIcon = (status: RestockStatus) => {
  switch (status) {
    case RestockStatus.QUOTE:
      return PendingActionsIcon;
    case RestockStatus.ORDERED:
      return ShoppingCartCheckoutIcon;
    case RestockStatus.DELIVERED:
      return CheckCircleOutlineIcon;
    case RestockStatus.PAID:
      return PaidIcon;
    case RestockStatus.CANCELLED:
      return CancelOutlinedIcon;
    default:
      return LocalShippingIcon;
  }
};

const filterOptions: FilterOption[] = [
  {
    field: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: RestockStatus.QUOTE, label: 'Quote' },
      { value: RestockStatus.ORDERED, label: 'Ordered' },
      { value: RestockStatus.DELIVERED, label: 'Delivered' },
      { value: RestockStatus.PAID, label: 'Paid' },
      { value: RestockStatus.CANCELLED, label: 'Cancelled' },
    ],
  },
];

const getExportColumns = (): ColumnConfig[] => [
  { field: 'orderNumber', headerName: 'Order Number' },
  {
    field: 'supplier',
    headerName: 'Supplier',
    valueFormatter: (value) => value?.name || '-',
  },
  {
    field: 'location',
    headerName: 'Location',
    valueFormatter: (value) => value?.name || '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    valueFormatter: (value) => statusLabels[value as RestockStatus] || value,
  },
  {
    field: 'subtotal',
    headerName: 'Subtotal',
    valueFormatter: (value) => `${Number(value || 0).toFixed(2)} DA`,
  },
  {
    field: 'tax',
    headerName: 'Tax',
    valueFormatter: (value) => `${Number(value || 0).toFixed(2)} DA`,
  },
  {
    field: 'totalAmount',
    headerName: 'Total',
    valueFormatter: (value) => `${Number(value || 0).toFixed(2)} DA`,
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    valueFormatter: (value) => new Date(value).toLocaleDateString(),
  },
];

interface RestocksListProps {
  onCreateClick?: () => void;
  onOrderClick?: (order: Restock) => void;
}

export const RestocksList = ({ onCreateClick, onOrderClick }: RestocksListProps) => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const statusFilter = activeFilters.status as RestockStatus | undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['restocks', page, pageSize, searchValue, statusFilter],
    queryFn: () => fetchRestocks(page, pageSize, searchValue, statusFilter),
  });

  const filteredData = useMemo(() => {
    return data?.data || [];
  }, [data?.data]);

  const stats = useMemo(() => {
    const total = filteredData.length;
    const quote = filteredData.filter((item) => item.status === RestockStatus.QUOTE).length;
    const ordered = filteredData.filter((item) => item.status === RestockStatus.ORDERED).length;
    const delivered = filteredData.filter((item) => item.status === RestockStatus.DELIVERED).length;
    const paid = filteredData.filter((item) => item.status === RestockStatus.PAID).length;
    const cancelled = filteredData.filter((item) => item.status === RestockStatus.CANCELLED).length;
    const totalAmount = filteredData.reduce((sum, item) => sum + (item.totalAmount || 0), 0);

    return { total, quote, ordered, delivered, paid, cancelled, totalAmount };
  }, [filteredData]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const handleRowClick = (order: Restock) => {
    if (onOrderClick) {
      onOrderClick(order);
    } else {
      navigate(`/app/restocks/${order.id}`);
    }
  };

  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick();
    } else {
      navigate('/app/restocks/create');
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableColumns: Column<Restock>[] = [
    {
      field: 'orderNumber',
      headerName: 'Order Number',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: alpha(palette.primary, 0.1),
              border: `1px solid ${palette.primary}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 20, color: palette.primary }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
              }}
            >
              {row.supplier?.name || 'Unknown Supplier'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      render: (value) => (
        <Typography
          sx={{
            color: colors.textPrimary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.sm,
          }}
        >
          {value?.name || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      render: (value) => {
        const statusColor = getStatusColor(value as RestockStatus);
        const StatusIcon = getStatusIcon(value as RestockStatus);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                backgroundColor: alpha(statusColor, 0.1),
                border: `1px solid ${statusColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StatusIcon sx={{ fontSize: 14, color: statusColor }} />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: statusColor,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
              }}
            >
              {statusLabels[value as RestockStatus] || value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'totalAmount',
      headerName: 'Total',
      render: (value) => (
        <Typography
          sx={{
            color: colors.textPrimary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.bold,
          }}
        >
          {Number(value || 0).toLocaleString()} DA
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      render: (value) => (
        <Typography
          sx={{
            color: colors.textSecondary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
          }}
        >
          {new Date(value).toLocaleDateString()}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ ...animations.slideDown, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LocalShippingIcon sx={{ color: palette.primary, fontSize: 24 }} />
            <Typography
              variant="overline"
              sx={{
                color: palette.primary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              SUPPLIER OPERATIONS
            </Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            RESTOCKS
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          sx={{
            backgroundColor: palette.primary,
            color: '#FFFFFF',
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            fontSize: typography.fontSize.sm,
            textTransform: 'uppercase',
            border: `2px solid ${palette.primary}`,
            borderRadius: theme.radius.xs,
            px: 3,
            py: 1.5,
            '&:hover': {
              backgroundColor: alpha(palette.primary, 0.9),
              borderColor: palette.primary,
            },
          }}
        >
          NEW ORDER
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
        {/* Total Orders */}
        <Card
          sx={{
            ...animations.staggeredCard(0, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.primary,
            },
            '&:hover': {
              borderColor: palette.primary,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.primary, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              TOTAL ORDERS
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.primary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.total}
            </Typography>
          </Box>
        </Card>

        {/* Quotes */}
        <Card
          sx={{
            ...animations.staggeredCard(1, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.warning,
            },
            '&:hover': {
              borderColor: palette.warning,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.warning, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              QUOTES
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.warning,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.quote}
            </Typography>
          </Box>
        </Card>

        {/* Ordered */}
        <Card
          sx={{
            ...animations.staggeredCard(2, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.info,
            },
            '&:hover': {
              borderColor: palette.info,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.info, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              ORDERED
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.info,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.ordered}
            </Typography>
          </Box>
        </Card>

        {/* Delivered */}
        <Card
          sx={{
            ...animations.staggeredCard(3, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
            '&:hover': {
              borderColor: palette.success,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.success, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              DELIVERED
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.success,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.delivered}
            </Typography>
          </Box>
        </Card>

        {/* Paid */}
        <Card
          sx={{
            ...animations.staggeredCard(4, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.accent,
            },
            '&:hover': {
              borderColor: palette.accent,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.accent, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              PAID
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.accent,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['3xl'],
                mt: 1,
              }}
            >
              {stats.paid}
            </Typography>
          </Box>
        </Card>

        {/* Total Amount */}
        <Card
          sx={{
            ...animations.staggeredCard(5, 0.2),
            position: 'relative',
            backgroundColor: colors.surfaceElevated,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.xs,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
            '&:hover': {
              borderColor: palette.success,
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.success, 0.2)}`,
            },
          }}
        >
          <Box sx={{ p: 2.5, pt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                letterSpacing: typography.letterSpacing.widest,
                textTransform: 'uppercase',
              }}
            >
              TOTAL VALUE
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: palette.success,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                fontSize: typography.fontSize['2xl'],
                mt: 1,
              }}
            >
              {stats.totalAmount.toLocaleString()} DA
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* Industrial Table Card */}
      <Card
        sx={{
          ...animations.slideUp(0.68),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
        }}
      >
        <Box sx={{ p: 2, borderBottom: `2px solid ${colors.border}` }}>
          <TableToolbar
            title="Restocks"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Search by order number..."
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={getExportColumns()}
            exportFileName="restocks"
          />
        </Box>

        <ResponsiveTable
          columns={tableColumns}
          rows={filteredData}
          loading={isLoading}
          emptyMessage="NO ORDERS FOUND"
          primaryField="orderNumber"
          getPrimaryText={(row) => row.orderNumber}
          getSecondaryText={(row) => row.supplier?.name || 'Unknown Supplier'}
          onRowClick={handleRowClick}
        />

        <TablePagination
          component="div"
          count={data?.total || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>
    </Box>
  );
};

export default RestocksList;
